import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { TitleInteractor, type TitleInteractorViewState } from './TitleInteractor';
import { TreeInteractor } from '../tree-domain/TreeInteractor';
import { useTreeState } from '../tree-domain/TreeInteractorContext';
import { TitleBar } from '../chrome/TitleBar';

interface TreeTitleBarProps {
  treeInteractor: TreeInteractor;
  isEditMode?: boolean;
}

export const TreeTitleBar: React.FC<TreeTitleBarProps> = ({ treeInteractor, isEditMode = false }) => {
  const treeState = useTreeState();
  const isLoading = treeState.isLoading ?? false;
  
  const interactor = useMemo(() => {
    const titleInteractor = new TitleInteractor(treeInteractor);
    titleInteractor.setEditMode(isEditMode);
    return titleInteractor;
  }, [treeInteractor, isEditMode]);

  const [viewState, setViewState] = useState<TitleInteractorViewState>(() => interactor.getViewState());

  const updateViewState = useCallback(() => {
    setViewState(interactor.getViewState());
  }, [interactor]);

  // Update viewState when isEditMode or isLoading changes
  useEffect(() => {
    interactor.setEditMode(isEditMode);
    interactor.setIsLoading(isLoading);
    updateViewState();
  }, [isEditMode, isLoading, interactor, updateViewState]);

  // Update viewState when tree name changes in treeState
  // This ensures we catch updates from TreeInteractor's callback
  const lastTreeNameRef = React.useRef<string>(treeState.tree.name);
  useEffect(() => {
    const currentName = treeState.tree.name;
    if (currentName !== lastTreeNameRef.current) {
      lastTreeNameRef.current = currentName;
      updateViewState();
    }
  }, [treeState.tree.name, updateViewState]);

  const handleEditClick = () => {
    interactor.startEditing();
    updateViewState();
  };

  const handleSaveClick = async () => {
    try {
      await interactor.saveTreeName();
      updateViewState();
    } catch (error) {
      // Error is already handled in ViewState
      updateViewState();
      console.error('Failed to save tree name:', error);
    }
  };

  const handleCancelClick = () => {
    interactor.cancelEditing();
    updateViewState();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveClick();
    } else if (e.key === 'Escape') {
      handleCancelClick();
    }
  };

  // If not editing and no edit button, just show simple title
  if (!viewState.isEditing && !viewState.showEditButton) {
    return <TitleBar title={viewState.treeName} />;
  }

  return (
    <div className="title-bar">
      {viewState.isEditing ? (
        <>
          <input
            type="text"
            className={`title-bar-input ${viewState.treeNameError ? 'form-input-error' : ''}`}
            value={viewState.treeName}
            onChange={(e) => {
              interactor.setTreeName(e.target.value);
              updateViewState();
            }}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={viewState.isSaving}
          />
          {viewState.treeNameError && (
            <div className="form-error">{viewState.treeNameError}</div>
          )}
          {viewState.showSaveButton && (
            <button 
              onClick={handleSaveClick} 
              className="btn btn-primary"
              disabled={viewState.isSaving}
            >
              {viewState.isSaving ? 'Saving...' : 'Save'}
            </button>
          )}
          {viewState.showCancelButton && (
            <button 
              onClick={handleCancelClick} 
              className="btn btn-secondary"
              disabled={viewState.isSaving}
            >
              Cancel
            </button>
          )}
        </>
      ) : (
        <>
          <h1>{viewState.treeName}</h1>
          {viewState.showEditButton && (
            <button 
              onClick={handleEditClick} 
              className="btn btn-secondary"
            >
              Edit
            </button>
          )}
        </>
      )}
      {viewState.error && (
        <div className="form-error">{viewState.error}</div>
      )}
    </div>
  );
};

